import { useCallback, useEffect, useRef, useState } from "react";
import { formatDate } from "./core";
import type { StaffPaneProps } from "./console-types";
import {
  PROMPT_PATCH_LIMITS,
  PROMPT_PATCH_POLL_STATUSES,
  isOpenPromptPatch,
  type PromptPatchRequestDto
} from "../../shared/prompt-patch";
import "./prompt-patch-card.css";

// F170 "Zapracovat do promptu" (Tomas Pohl 2026-10-05). The partner checks
// beta podněty in the queue above; this card sends them, then shows the
// request in plain Czech: waiting for Alex, the proposal (per-podnět category
// + before/after hunks the partner may edit), approve / reject, writing,
// worked in. The proposal and the write run on the Mac Mini.

const POLL_MS = 15_000;
const KNOWN_ERRORS = ["already_open", "invalid_feedback", "state_conflict"];

type Props = StaffPaneProps & {
  selectedIds: string[];
  onSubmitted: () => void;
  /** Tells the queue whether a request is open (checkboxes go quiet). */
  onOpenChange?: (open: boolean) => void;
};

function errorKey(code: string | undefined): string {
  return `staffConsole.promptPatch.errors.${code && KNOWN_ERRORS.includes(code) ? code : "generic"}`;
}

export function PromptPatchCard(props: Props) {
  const t = props.t;
  const clientId = props.detail.client.id;
  const base = `/api/staff/clients/${encodeURIComponent(clientId)}/prompt-patch`;
  // undefined = not loaded yet, null = no request for this client yet.
  const [request, setRequest] = useState<PromptPatchRequestDto | null | undefined>(undefined);
  const [loadFailed, setLoadFailed] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [editing, setEditing] = useState<number | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const lastStatus = useRef<string | null>(null);
  const { onOpenChange, refresh } = props;

  const load = useCallback(async () => {
    try {
      const response = await fetch(base, { credentials: "include" });
      if (!response.ok) { setLoadFailed(true); return; }
      const body = await response.json() as { request: PromptPatchRequestDto | null };
      setLoadFailed(false);
      setRequest(body.request);
    } catch {
      setLoadFailed(true);
    }
  }, [base]);

  useEffect(() => { void load(); }, [load]);

  const status = request?.status ?? null;
  const open = Boolean(status && isOpenPromptPatch(status));
  const polling = Boolean(status && PROMPT_PATCH_POLL_STATUSES.includes(status));
  useEffect(() => { onOpenChange?.(open); }, [open, onOpenChange]);
  useEffect(() => {
    if (!polling) return;
    const timer = window.setInterval(() => { void load(); }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [polling, load]);
  // The write resolved podněty in the queue above: pull the detail once.
  useEffect(() => {
    if (lastStatus.current && lastStatus.current !== "applied" && status === "applied") void refresh();
    lastStatus.current = status;
  }, [status, refresh]);
  // A new proposal starts from the machine's own "po" text.
  useEffect(() => { setEdits({}); setEditing(null); setRejecting(false); }, [request?.id, status]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const response = await props.run(base, { method: "POST", body: { feedbackIds: props.selectedIds, note: note.trim() || undefined } }, { refresh: false });
      const body = await response.json().catch(() => null) as { request?: PromptPatchRequestDto; error?: string } | null;
      if (response.ok && body?.request) {
        setRequest(body.request);
        setNote("");
        props.onSubmitted();
        return;
      }
      setError(errorKey(body?.error));
    } finally {
      setBusy(false);
    }
  }

  async function decide(decision: "approve" | "reject") {
    if (!request?.proposal) return;
    setBusy(true);
    setError(null);
    try {
      const editedPatch = decision === "approve" && Object.keys(edits).length > 0
        ? request.proposal.patch.map((hunk, index) => ({ find: hunk.find, replace: edits[index] ?? hunk.replace }))
        : undefined;
      const response = await props.run(`${base}/${encodeURIComponent(request.id)}/decision`, {
        method: "POST",
        body: { decision, editedPatch, note: decision === "reject" ? rejectNote.trim() || undefined : undefined }
      }, { refresh: false });
      const body = await response.json().catch(() => null) as { request?: PromptPatchRequestDto; error?: string } | null;
      if (response.ok && body?.request) { setRequest(body.request); setRejectNote(""); return; }
      setError(errorKey(body?.error));
      if (response.status === 409) void load();
    } finally {
      setBusy(false);
    }
  }

  const podnetText = (feedbackId: string): string | null => {
    const row = props.detail.feedback.find((item) => String(item.id) === feedbackId);
    const text = row ? row.text ?? row.transcript : null;
    return typeof text === "string" && text.trim() ? text : null;
  };

  const proposal = request?.proposal ?? null;
  const shownPatch = request?.editedPatch ?? proposal?.patch ?? [];
  return (
    <article className="staff-card prompt-patch-card" data-prompt-patch="true">
      <h3>{t("staffConsole.promptPatch.title")}</h3>
      {!open && <p className="staff-empty">{t("staffConsole.promptPatch.hint")}</p>}

      {loadFailed && request === undefined && <p className="staff-empty">{t("staffConsole.promptPatch.states.unavailable")}</p>}

      {request && (
        <section className="prompt-patch-request" data-prompt-patch-state={request.status}>
          <p className="prompt-patch-state" role="status">
            {polling && <span className="loading-pulse" />}
            {t(`staffConsole.promptPatch.states.${request.status}`)}
          </p>
          <p className="staff-card-hint">
            {t("staffConsole.promptPatch.requestedAt")
              .replace("{date}", formatDate(request.createdAt, props.locale))
              .replace("{count}", String(request.feedbackIds.length))}
          </p>
          {request.partnerNote && <p className="staff-card-hint">{t("staffConsole.promptPatch.partnerNote").replace("{note}", request.partnerNote)}</p>}
          {request.resultNote && (request.status === "applied" || request.status === "failed" || request.status === "rejected") && (
            <p className={request.status === "failed" ? "message error-message" : "staff-card-hint"}>
              {t("staffConsole.promptPatch.resultNote").replace("{note}", request.resultNote)}
            </p>
          )}
          {request.status === "applied" && <p className="staff-card-hint">{t("staffConsole.promptPatch.appliedHint")}</p>}

          {proposal && (request.status === "proposed" || request.status === "approved" || request.status === "applying" || request.status === "applied") && (
            <>
              {proposal.summary && <p><strong>{t("staffConsole.promptPatch.summaryLabel")}:</strong> {proposal.summary}</p>}
              <h4>{t("staffConsole.promptPatch.itemsTitle")}</h4>
              <ul className="prompt-patch-items">
                {proposal.items.map((item) => (
                  <li key={item.feedbackId} className="prompt-patch-item">
                    <q>{podnetText(item.feedbackId) ?? t("staffConsole.promptPatch.unknownPodnet")}</q>
                    <span className="prompt-patch-category" data-category={item.category}>
                      {t(`staffConsole.promptPatch.categories.${item.category}`)}
                    </span>
                    {item.reason && <span>{item.reason}</span>}
                  </li>
                ))}
              </ul>
              <h4>{t("staffConsole.promptPatch.patchTitle")}</h4>
              {shownPatch.length === 0 && <p className="staff-empty">{t("staffConsole.promptPatch.noPatch")}</p>}
              <ol className="prompt-patch-hunks">
                {shownPatch.map((hunk, index) => {
                  const why = proposal.patch[index]?.why;
                  const after = edits[index] ?? hunk.replace;
                  const editable = request.status === "proposed";
                  return (
                    <li key={index} className="prompt-patch-hunk" data-prompt-patch-hunk={index}>
                      <span className="prompt-patch-label">{t("staffConsole.promptPatch.before")}</span>
                      <p className="prompt-patch-side before">{hunk.find}</p>
                      <span className="prompt-patch-label">
                        {t("staffConsole.promptPatch.after")}
                        {(edits[index] !== undefined || request.editedPatch) && ` · ${t("staffConsole.promptPatch.edited")}`}
                      </span>
                      {editing === index ? (
                        <textarea
                          rows={Math.min(14, Math.max(3, after.split("\n").length + 1))}
                          value={after}
                          maxLength={PROMPT_PATCH_LIMITS.patchTextChars}
                          aria-label={t("staffConsole.promptPatch.after")}
                          onChange={(event) => setEdits((current) => ({ ...current, [index]: event.target.value }))}
                        />
                      ) : (
                        <p className="prompt-patch-side after">{after}</p>
                      )}
                      {why && <p className="staff-card-hint">{t("staffConsole.promptPatch.why").replace("{why}", why)}</p>}
                      {editable && (
                        <button type="button" className="quiet-link" disabled={busy}
                          onClick={() => setEditing(editing === index ? null : index)}>
                          {editing === index ? t("staffConsole.promptPatch.editDone") : t("staffConsole.promptPatch.edit")}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ol>
              {request.status === "proposed" && (rejecting ? (
                <div className="prompt-patch-compose">
                  <label>
                    {t("staffConsole.promptPatch.rejectNoteLabel")}
                    <textarea rows={2} value={rejectNote} maxLength={PROMPT_PATCH_LIMITS.noteChars}
                      onChange={(event) => setRejectNote(event.target.value)} />
                  </label>
                  <div className="prompt-patch-actions">
                    <button type="button" className="secondary-button compact-button" disabled={busy} onClick={() => void decide("reject")}>
                      {busy ? t("staffConsole.promptPatch.deciding") : t("staffConsole.promptPatch.confirmReject")}
                    </button>
                    <button type="button" className="quiet-link" disabled={busy} onClick={() => setRejecting(false)}>
                      {t("staffConsole.promptPatch.cancel")}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="prompt-patch-actions">
                  <button type="button" className="primary-button compact-button" disabled={busy} onClick={() => void decide("approve")}>
                    {busy ? t("staffConsole.promptPatch.deciding") : t("staffConsole.promptPatch.approve")}
                  </button>
                  <button type="button" className="secondary-button compact-button" disabled={busy} onClick={() => setRejecting(true)}>
                    {t("staffConsole.promptPatch.reject")}
                  </button>
                </div>
              ))}
            </>
          )}
        </section>
      )}

      {error && <p className="message error-message" role="alert">{t(error)}</p>}

      {open ? (
        request?.status !== "proposed" && <p className="staff-card-hint">{t("staffConsole.promptPatch.openBlocks")}</p>
      ) : request !== undefined && (
        <div className="prompt-patch-compose">
          <p className="staff-card-hint" data-prompt-patch-selected={props.selectedIds.length}>
            {props.selectedIds.length > 0
              ? t("staffConsole.promptPatch.selectedCount").replace("{count}", String(props.selectedIds.length))
              : t("staffConsole.promptPatch.selectHint")}
          </p>
          <label>
            {t("staffConsole.promptPatch.noteLabel")}
            <textarea rows={2} value={note} maxLength={PROMPT_PATCH_LIMITS.noteChars}
              placeholder={t("staffConsole.promptPatch.notePlaceholder")}
              onChange={(event) => setNote(event.target.value)} />
          </label>
          <div className="prompt-patch-actions">
            <button type="button" className="primary-button compact-button"
              disabled={busy || props.selectedIds.length === 0}
              onClick={() => void submit()}>
              {busy ? t("staffConsole.promptPatch.submitting") : t("staffConsole.promptPatch.submit")}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
